import type { KeyboardEvent } from 'react';
import type { VodSlideMarker } from 'src/generated/graphql-manager';
import type { Slide } from 'src/viewer/components/slide-preview/slide-preview';

import { ChevronIcon, IconButton } from '@movingimage-evp/mi-ui-component-library';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { offsetInMsToString } from '../event-summary-cue-points/format-time';

import styles from './event-summary-chapters.module.css';

type SlideMarkerDropdownProps = {
  id?: string;
  slideMarkers?: VodSlideMarker[];
  slides?: Slide[];
  onSelect?: (slideMarker: VodSlideMarker) => void;
};

export function SlideMarkerDropdown({ id, slideMarkers = [], slides = [], onSelect }: SlideMarkerDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation();

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (marker: VodSlideMarker) => {
    onSelect?.(marker);
    setIsOpen(false);
  };

  const handleKeyDown = (event: KeyboardEvent, marker: VodSlideMarker) => {
    if (event.key === 'Enter' || event.key === ' ') handleSelect(marker);
  };

  if (slideMarkers.length === 0) return null;

  const testId = id ? `${id}-` : '';

  return (
    <div className={styles.slideMarkerDropdown} ref={dropdownRef}>
      <IconButton
        naked
        small
        data-testid={`${testId}slide-marker-dropdown-toggle`}
        aria-label={t('manager.eventSummary.chapters.selectFromSlides')}
        className={styles.slideMarkerDropdownToggle}
        onClick={() => setIsOpen(!isOpen)}
      >
        <ChevronIcon className={isOpen ? styles.rotateUp : styles.rotateDown} />
      </IconButton>

      {isOpen && (
        <ul
          className={styles.slideMarkerDropdownList}
          data-testid={`${testId}slide-marker-dropdown-list`}
          tabIndex={-1}
        >
          {slideMarkers.map((marker) => {
            const slide = slides.find((slide) => slide.groupId === marker.groupId && slide.index === marker.index);
            const timeString = offsetInMsToString(marker.offsetInMs, true);

            return (
              <li
                key={`${marker.groupId}-${marker.index}`}
                data-testid={`${testId}slide-marker-item-${marker.index}`}
                role="button"
                tabIndex={0}
                className={styles.slideMarkerDropdownItem}
                onClick={() => handleSelect(marker)}
                onKeyDown={(event) => handleKeyDown(event, marker)}
              >
                <div className={styles.slideMarkerThumbnail}>
                  {slide?.thumbnailDownloadUrl && (
                    <img
                      src={slide.thumbnailDownloadUrl}
                      alt={t('manager.eventSummary.chapters.slideNumber', { number: marker.index })}
                      className={styles.slideThumbnail}
                    />
                  )}
                </div>

                <div className={styles.slideMarkerInfo}>
                  <span className={styles.slideMarkerNumber}>
                    {t('manager.eventSummary.chapters.slideNumber', { number: marker.index })}
                  </span>

                  <span className={styles.slideMarkerTime}>{timeString}</span>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
